import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { AdminLayout } from './components/layout/AdminLayout';
import { LoginPage } from './pages/auth/LoginPage';

// Fleet Ops Pages
import { OverviewPage } from './pages/fleet/OverviewPage';
import { CustomerFleetPage } from './pages/fleet/CustomerFleetPage';
import { CamerasPage } from './pages/fleet/CamerasPage';
import { CameraMapPage } from './pages/fleet/CameraMapPage';
import { OfflineTriagePage } from './pages/fleet/OfflineTriagePage';
import { VersionsPage } from './pages/fleet/VersionsPage';
import { CloudStoragePage } from './pages/fleet/CloudStoragePage';
import { RecordingsPage } from './pages/fleet/RecordingsPage';
import { FacesPage } from './pages/fleet/FacesPage';
import { SimManagementPage } from './pages/fleet/SimManagementPage';
import { AlertsPage } from './pages/fleet/AlertsPage';

// Customers & Billing Pages
import { CustomersPage } from './pages/billing/CustomersPage';
import { SubscriptionsPage } from './pages/billing/SubscriptionsPage';
import { PaymentsPage } from './pages/billing/PaymentsPage';
import { PlansPage } from './pages/billing/PlansPage';
import { InventoryPage } from './pages/billing/InventoryPage';
import { OrdersPage } from './pages/billing/OrdersPage';

// Support Pages
import { TicketsPage } from './pages/support/TicketsPage';

// Governance & Security Pages
import { AdminsPage } from './pages/governance/AdminsPage';
import { AuditLogPage } from './pages/governance/AuditLogPage';
import { BroadcastPage } from './pages/governance/BroadcastPage';
import { AnalyticsPage } from './pages/governance/AnalyticsPage';
import { AIUsagePage } from './pages/governance/AIUsagePage';
import { SecurityPage } from './pages/account/SecurityPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 30,
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted, #888)', fontSize: '14px' }}>
        Restoring session…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/customer-fleets" element={<CustomerFleetPage />} />
        <Route path="/cameras" element={<CamerasPage />} />
        <Route path="/camera-versions" element={<VersionsPage />} />
        <Route path="/camera-map" element={<CameraMapPage />} />
        <Route path="/offline-triage" element={<OfflineTriagePage />} />
        <Route path="/cloud-storage" element={<CloudStoragePage />} />
        <Route path="/recordings" element={<RecordingsPage />} />
        <Route path="/sim-management" element={<SimManagementPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/faces" element={<FacesPage />} />

        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/orders" element={<OrdersPage />} />

        <Route path="/tickets" element={<TicketsPage />} />

        <Route path="/admins" element={<AdminsPage />} />
        <Route path="/audit-log" element={<AuditLogPage />} />
        <Route path="/broadcast" element={<BroadcastPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/ai-usage" element={<AIUsagePage />} />
        <Route path="/security" element={<SecurityPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WebSocketProvider>
          <HashRouter>
            <AppRoutes />
          </HashRouter>
        </WebSocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
